// React
import { ReactElement, useEffect, useState } from 'react';
// Types
import { Slice } from '@utmb/types/slice-handler';
// Styles
import styles from './bus-reservation.module.scss';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'next-i18next';
import { BusReservationForm } from '@utmb/features/bus-reservation/BusReservationForm';
import SliceBusReservationView from '@utmb/types/view/SliceBusReservationView';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { Button } from '@utmb/design-system/button/Button';
import * as React from 'react';
//import { BusTickets } from '@utmb/features/bus-reservation/BusTickets';
import { useRouter } from 'next/router';
import { fetchTheAPI } from '@utmb/utils/utmb';
import { useTenancy } from '@utmb/contexts/TenancyContext';
import { Font } from '@utmb/design-system/text/font';
import { StripeReturnType } from '@utmb/features/bus-reservation/BusReservationTypes';
import { Slide, ToastContainer } from 'react-toastify';
import dynamic from 'next/dynamic';
import BusParametersDataView from '@utmb/types/view/BusParametersDataView';

const BusTicketsDynamic = dynamic(() => import('@utmb/features/bus-reservation/BusTickets'), {
    ssr: false,
    loading: () => <div />,
});

interface authFromGetType {
    email: string;
    reservationNumber: string;
}
export const BusReservation = (props: Slice<SliceBusReservationView>): ReactElement => {
    const { id, forceClosed } = props;
    const { t } = useTranslation();

    const { tenantId } = useTenancy();
    const [formState, setFormState] = useState(forceClosed ? 'tickets' : 'reservation');
    const [messageReturn, setMessageReturn] = useState('');
    const [success, setSuccess] = useState(false);
    const [stripeReturn, setStripeReturn] = useState<StripeReturnType | null | undefined>();
    const [authFromGet, setAuthFromGet] = useState<authFromGetType | null | undefined>();
    const [busParams, setBusParams] = useState<BusParametersDataView>();

    const handleReservationSelect = () => {
        setMessageReturn('');
        setSuccess(false);
        setStripeReturn(null);
        setFormState(forceClosed ? 'tickets' : 'reservation');
    };

    async function getStripe(): Promise<void> {
        const stripeReturnData: StripeReturnType = (await fetchTheAPI({
            tenantId,
            isClient: true,
            path: `/payment/stripe/${router.query.payment_intent}`,
        })) as StripeReturnType;
        setStripeReturn(stripeReturnData);
    }

    const router = useRouter();
    const redirectStatus = router?.query?.redirect_status ?? null;
    const formStateGet = router?.query?.formState ?? null;

    const getBusParams = React.useCallback(async () => {
        const myBusParams: BusParametersDataView = (await fetchTheAPI({
            tenantId,
            isClient: true,
            path: `/bus/parameters`,
            params: { lang: router.locale as string },
        })) as BusParametersDataView;

        setBusParams(myBusParams);
    }, []);

    useEffect(() => {
        getBusParams();
    }, []);

    useEffect(() => {
        if (formStateGet === 'tickets') {
            setFormState('tickets');
            if (router?.query?.reservationNumber && router?.query?.reservationEmail) {
                const authData: authFromGetType = {
                    reservationNumber: router?.query?.reservationNumber.toString(),
                    email: router?.query?.reservationEmail.toString(),
                };
                setAuthFromGet(authData);
            }
        }
    }, [formStateGet]);

    useEffect(() => {
        if (redirectStatus) {
            switch (redirectStatus) {
                case 'succeeded':
                    setMessageReturn(t('busReservation.paymentSucceed'));
                    setSuccess(true);
                    setFormState('tickets');
                    getStripe();
                    break;
                case 'processing':
                    setMessageReturn(t('busReservation.paymentProcessingMsg'));
                    setSuccess(false);
                    setFormState('tickets');
                    break;
                case 'requires_payment_method':
                    setMessageReturn(t('busReservation.paymentFailed'));
                    setSuccess(false);
                    setFormState('tickets');
                    break;
                default:
                    setMessageReturn(t('busReservation.paymentWrong'));
                    setSuccess(false);
                    setFormState('Something went wrong.');
                    break;
            }
        }
    }, [redirectStatus]);

    return (
        <section id={id} className={styles.shuttleBooking}>
            {messageReturn ? (
                <Font.OxaniumMedium
                    as={'div'}
                    desktop="16"
                    mobile="16"
                    className={styles.reservationNotFound}
                    color={success ? getColorStyle('black') : getColorStyle('red')}
                >
                    {messageReturn}
                </Font.OxaniumMedium>
            ) : (
                <></>
            )}

            <div className={styles.shuttleSelector}>
                {!forceClosed ? (
                    <Button.Default
                        color={formState === 'reservation' ? getColorStyle(ColorTypesEnum.SECONDARY) : getColorStyle(ColorTypesEnum.PRIMARY)}
                        bgcolor={formState === 'reservation' ? getColorStyle(ColorTypesEnum.PRIMARY) : getColorStyle(ColorTypesEnum.SECONDARY)}
                        label={t('busReservation.reservation')}
                        onPress={() => handleReservationSelect()}
                    />
                ) : null}
                <Button.Default
                    color={formState === 'tickets' ? getColorStyle(ColorTypesEnum.SECONDARY) : getColorStyle(ColorTypesEnum.PRIMARY)}
                    bgcolor={formState === 'tickets' ? getColorStyle(ColorTypesEnum.PRIMARY) : getColorStyle(ColorTypesEnum.SECONDARY)}
                    label={t('busReservation.getTickets')}
                    onPress={() => setFormState('tickets')}
                />
            </div>
            {busParams ? (
                <>
                    {formState === 'reservation' && !forceClosed ? (
                        <BusReservationForm busParams={busParams} id={id} />
                    ) : (
                        <BusTicketsDynamic busParams={busParams} stripeReturn={stripeReturn} authFromGet={authFromGet} />
                    )}
                </>
            ) : (
                <></>
            )}
            <ToastContainer position="bottom-right" hideProgressBar transition={Slide} />
        </section>
    );
};
